/* Native scroll; no wheel/touch interception, dependencies, or idle animation loop. */
(() => {
  if (customElements.get('ns-about-scroll')) return;
  const clamp = (n, min = 0, max = 1) => Math.min(max, Math.max(min, n));
  const pad = n => String(n).padStart(2, '0');
  class NSAboutScroll extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.query = matchMedia('(min-width: 900px) and (min-height: 650px) and (prefers-reduced-motion: no-preference)');
      this.intensity = clamp(Number(this.dataset.intensity) || 0);
      this.story = this.querySelector('[data-story]');
      this.track = this.querySelector('[data-story-track]');
      this.scenes = [...this.querySelectorAll('[data-scene]')];
      this.products = this.querySelector('[data-products]');
      this.productPin = this.querySelector('[data-product-pin]');
      this.productItems = [...this.querySelectorAll('[data-product]')];
      this.heroArt = this.querySelector('[data-hero-art]');
      this.heroCopy = this.querySelector('[data-hero-copy]');
      this.count = this.querySelector('[data-story-count]');
      this.progress = this.querySelector('[data-story-progress]');
      this.productCount = this.querySelector('[data-product-count]');
      this.productLabel = this.querySelector('[data-product-label]');
      this.schedule = () => { if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); }); };
      this.onResize = () => { clearTimeout(this.resizeTimer); this.resizeTimer = setTimeout(() => this.configure(), 100); };
      this.query.addEventListener('change', () => this.configure(), options);
      window.addEventListener('resize', this.onResize, { ...options, passive: true });
      window.addEventListener('scroll', this.schedule, { ...options, passive: true });
      document.addEventListener('shopify:section:unload', e => { if (e.target.contains(this)) this.teardown(); }, options);
      document.addEventListener('shopify:section:reorder', () => this.configure(), options);
      document.addEventListener('shopify:block:select', e => {
        const block = this.querySelector(`[data-block-id="${CSS.escape(e.detail?.blockId || '')}"]`);
        if (block) block.scrollIntoView({ block: 'center', behavior: 'instant' });
      }, options);
      this.addEventListener('focusin', e => {
        if (!this.motion) return;
        const scene = e.target.closest('[data-scene]');
        if (scene && this.story && this.storyTravel) {
          const index = this.scenes.indexOf(scene);
          const distance = this.sceneOffsets[index] || 0;
          window.scrollTo({ top: this.story.getBoundingClientRect().top + scrollY - this.offset + distance, behavior: 'instant' });
        }
      }, options);
      this.querySelectorAll('[data-product-jump]').forEach(button => button.addEventListener('click', () => {
        if (!this.motion) return;
        const index = Number(button.dataset.productJump);
        window.scrollTo({ top: this.products.getBoundingClientRect().top + scrollY + this.pinStart + index * this.productTravel - this.offset, behavior: 'instant' });
      }, options));
      // A newly inserted editor section needs a frame for its stylesheet and layout.
      this.setupFrame = requestAnimationFrame(() => this.configure());
      const heading = this.querySelector('.ns-about__products-heading');
      if (heading && typeof ResizeObserver !== 'undefined') {
        this.measureObserver = new ResizeObserver(entries => {
          const height = entries[0].contentRect.height;
          if (this.observedHeadingHeight !== height) { this.observedHeadingHeight = height; this.onResize(); }
        });
        this.measureObserver.observe(heading);
      }
    }
    configure() {
      this.observer?.disconnect();
      this.motion = this.query.matches && this.dataset.editor !== 'true' && this.productItems.length === 2;
      this.classList.toggle('is-motion', this.motion);
      this.style.removeProperty('--ns-story-travel');
      if (this.products) this.products.style.removeProperty('height');
      this.resetTransforms();
      this.offset = parseFloat(getComputedStyle(this).getPropertyValue('--ns-offset')) || 0;
      // Long merchant copy must remain readable, even when it outgrows a pinned scene.
      if (this.motion) {
        const tooTall = this.scenes.some(scene => {
          const copy = scene.querySelector('.ns-about__scene-copy');
          return copy && (copy.scrollHeight > scene.clientHeight + 2 || copy.getBoundingClientRect().height > scene.clientHeight + 2);
        }) || this.productItems.some(item => {
          const copy = item.querySelector('.ns-about__product-copy');
          return copy && copy.scrollHeight > this.productPin.clientHeight - 74;
        });
        const storyPin = this.querySelector('[data-story-pin]');
        const storyHeader = this.querySelector('.ns-about__story-header');
        let stageOverflow = false;
        if (storyPin && storyHeader && this.scenes.length) {
          const pinStyle = getComputedStyle(storyPin);
          const required = storyHeader.offsetHeight + this.scenes[0].clientHeight + parseFloat(pinStyle.paddingTop) + parseFloat(pinStyle.paddingBottom);
          stageOverflow = required > storyPin.clientHeight + 2;
        }
        if (tooTall || stageOverflow) { this.motion = false; this.classList.remove('is-motion'); }
      }
      if (this.motion) {
        this.sceneOffsets = this.scenes.map(scene => scene.offsetLeft - (this.scenes[0]?.offsetLeft || 0));
        this.sceneWidths = this.scenes.map(scene => scene.clientWidth);
        this.storyTravel = this.sceneOffsets.at(-1) || 0;
        this.style.setProperty('--ns-story-travel', `${this.storyTravel}px`);
        const stageHeight = this.productPin?.clientHeight || 0;
        // One stage for the first product, a short handoff, then the second product.
        this.productTravel = Math.max(500, stageHeight * 1.2);
        this.pinStart = (this.querySelector('.ns-about__products-heading')?.offsetHeight || 0) + (parseFloat(getComputedStyle(this.products).paddingTop) || 0);
        if (this.products) this.products.style.height = `${this.pinStart + stageHeight + this.productTravel}px`;
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => { if (entry.isIntersecting) { entry.target.classList.add('is-visible'); this.observer.unobserve(entry.target); } });
        }, { threshold: .08 });
        this.querySelectorAll('[data-reveal]').forEach(el => this.observer.observe(el));
      } else {
        this.querySelectorAll('[data-reveal]').forEach(el => el.classList.add('is-visible'));
      }
      this.update();
    }
    resetTransforms() {
      [this.track, this.heroArt, this.heroCopy].forEach(el => { if (el) { el.style.removeProperty('transform'); el.style.removeProperty('opacity'); } });
      this.scenes.forEach(scene => { scene.querySelector('.ns-about__image')?.style.removeProperty('transform'); });
      this.productItems.forEach(el => {
        el.style.removeProperty('transform'); el.style.removeProperty('opacity');
        el.style.removeProperty('visibility'); el.style.removeProperty('pointer-events');
        el.inert = false; el.removeAttribute('aria-hidden');
      });
    }
    update() {
      if (!this.isConnected) return;
      // Read all geometry before writing any animation style.
      const storyRect = this.story?.getBoundingClientRect();
      const pinRect = this.productPin?.getBoundingClientRect();
      const productsRect = this.products?.getBoundingClientRect();
      const heroRect = this.querySelector('[data-hero]')?.getBoundingClientRect();
      const sceneRects = !this.motion ? this.scenes.map(el => el.getBoundingClientRect()) : [];
      if (!this.motion) {
        if (sceneRects.length) {
          const target = innerHeight * .4;
          let nearest = 0, distance = Infinity;
          sceneRects.forEach((rect, i) => { const d = Math.abs(rect.top + Math.min(rect.height, innerHeight) / 2 - target); if (d < distance) { nearest = i; distance = d; } });
          this.setProgress(nearest, (nearest + 1) / this.scenes.length);
        }
        return;
      }
      if (storyRect && this.track) {
        const distance = clamp(this.offset - storyRect.top, 0, this.storyTravel);
        let nearest = 0, gap = Infinity;
        this.sceneOffsets.forEach((x, i) => { const d = Math.abs(x - distance); if (d < gap) { nearest = i; gap = d; } });
        this.track.style.transform = `translate3d(${-distance}px,0,0)`;
        this.setProgress(nearest, this.scenes.length > 1 ? (1 + distance / (this.storyTravel || 1) * (this.scenes.length - 1)) / this.scenes.length : 1);
        this.scenes.forEach((scene, i) => {
          const delta = clamp((distance - this.sceneOffsets[i]) / Math.max(1, this.sceneWidths[i]), -1, 1);
          const image = scene.querySelector('.ns-about__image');
          if (image) image.style.transform = `scale(${1 + Math.abs(delta) * .035 * this.intensity})`;
        });
      }
      if (productsRect && pinRect) {
        const p = clamp((this.offset - productsRect.top - this.pinStart) / (this.productTravel || 1));
        const mix = clamp((p - .32) / .36);
        const ease = mix * mix * (3 - 2 * mix);
        const active = mix < .5 ? 0 : 1;
        this.productItems.forEach((el, i) => {
          const opacity = i === 0 ? 1 - ease : ease;
          el.style.opacity = String(opacity);
          el.style.transform = `translate3d(0,${(i === 0 ? -ease : 1 - ease) * 42 * this.intensity}px,0)`;
          el.style.visibility = opacity <= .001 ? 'hidden' : 'visible';
          el.style.pointerEvents = i === active ? 'auto' : 'none';
          el.inert = i !== active;
          el.setAttribute('aria-hidden', String(i !== active));
        });
        if (this.productCount) this.productCount.textContent = pad(active + 1);
        if (this.productLabel) this.productLabel.textContent = active ? 'COLOUR' : 'CLEANSE';
        this.querySelectorAll('[data-product-jump]').forEach((button, index) => button.setAttribute('aria-pressed', String(index === active)));
      }
      if (heroRect && this.heroArt) {
        const p = clamp(-heroRect.top / Math.max(1, heroRect.height));
        this.heroArt.style.transform = `translate3d(0,${p * 45 * this.intensity}px,0) scale(${1 + p * .07 * this.intensity})`;
        this.heroCopy.style.transform = `translate3d(0,${-p * 26 * this.intensity}px,0)`;
      }
    }
    setProgress(index, value) {
      if (this.count) this.count.textContent = pad(index + 1);
      if (this.progress) this.progress.style.transform = `scaleX(${value})`;
    }
    disconnectedCallback() { this.teardown(); }
    teardown() {
      this.abort?.abort(); this.abort = null;
      this.observer?.disconnect(); this.measureObserver?.disconnect();
      cancelAnimationFrame(this.setupFrame); cancelAnimationFrame(this.frame); this.frame = 0;
      clearTimeout(this.resizeTimer); this.resetTransforms();
      this.classList.remove('is-motion'); this.style.removeProperty('--ns-story-travel');
      this.products?.style.removeProperty('height');
    }
  }
  customElements.define('ns-about-scroll', NSAboutScroll);
})();
